<script setup>
/* eslint-disable no-unused-vars */
/* global defineProps, defineEmits */
// Components
import { getAllPages } from "@/utils/getAllPages";
import { normalizeSearchText } from "@/utils/textSearch";
import SharedDeleteConfirmDialog from "@/components/shared/SharedDeleteConfirmDialog.vue";
import HeaderTitle from "@/components/layout/HeaderTitle.vue";
import { useAutomaticSearch } from "@/app/composables/useAutomaticSearch";

// Services
// Services
import ResourceService from '@/api/services/resource.service';
import EmployeeService from '@/api/services/employee.service';
import Notification from '@/utils/plugins/NotificationService';

import { computed, getCurrentInstance, onMounted, ref } from "vue";
import { useUiStore } from "@/app/stores/uiStore";


const props = defineProps({});

const uiStore = useUiStore();

const instance = getCurrentInstance();

const departments = ref([]);

const department = ref({});

const submitted = ref(false);

const deleteDialogShown = ref(false);

const selectedDepartment = ref(null);


const departmentName = ref('');
const departmentManagers = ref([]);
const departmentEmail = ref('');
const departmentAcceptsTickets = ref(false);
const employees = ref([]);

const createOrUpdateDepartment = ref(false);
const savingDepartment = ref(false);

const searchFilterDepartmentName = ref('');
const { beginRequest, isCurrentRequest } = useAutomaticSearch(searchFilterDepartmentName, () => getDepartments());

function managerReference(manager) {
  if (typeof manager === 'string') return manager;
  return manager?.['@id'] || (manager?.id ? `/api/employees/${manager.id}` : null);
}

function managerName(manager) {
  if (!manager) return '—';
  return manager.fullName || employees.value.find(employee =>
    managerReference(employee) === managerReference(manager)
  )?.fullName || '—';
}

function departmentManagerNames(department) {
  const managers = department.managers ?? (department.manager ? [department.manager] : []);
  return managers.length ? managers.map(managerName).join(', ') : '—';
}

function departmentPayload() {
  return {
    name: departmentName.value.trim(),
    managers: departmentManagers.value,
    email: departmentEmail.value.trim() || null,
    acceptsTickets: departmentAcceptsTickets.value,
  };
}

async function getEmployees() {
  uiStore.startLoading();
  try {
    employees.value = await getAllPages(EmployeeService.getEmployees, {
      active: 1,
      employeeType: 'employee',
    });
  } finally {
    uiStore.stopLoading();
  }
}

async function getDepartments(searchQuery = searchFilterDepartmentName.value) {
  const params = searchQuery ? { name: searchQuery } : {};
  uiStore.startLoading();
  const requestId = beginRequest();
  try {
    const result = await getAllPages(ResourceService.getDepartments, params);
    if (isCurrentRequest(requestId)) departments.value = result;
  } finally {
    uiStore.stopLoading();
  }
}

function deleteDepartment(department) {
  ResourceService.deleteDepartment(department.id).then(() => {
    Notification.addNotification('Departamento eliminado correctamente.', 'success', 6000);
    selectedDepartment.value = null;
    deleteDialogShown.value = false;
    getDepartments();
  }).catch(() => {
    Notification.addNotification('Error al eliminar el departamento seleccionado', 'error', 6000);
    // this.$uiStore.setLoading(false);
    uiStore.stopLoading();
  });
}

function createDepartment() {
  // this.$uiStore.setLoading(true);
  uiStore.startLoading();
  return ResourceService.createDepartment(departmentPayload()).then(() => {
    Notification.addNotification('Departamento creado correctamente', 'success', 6000);
    // this.$uiStore.setLoading(false);
    uiStore.stopLoading();
    cancel();
  }).catch(() => {
    Notification.addNotification('Error al crear el departamento', 'error', 6000);
    // this.$uiStore.setLoading(false);
    uiStore.stopLoading();
  }).finally(() => {
    closeCreateOrUpdateDialog();
  });
}

function editDepartment(department) {
  selectedDepartment.value = department;
  departmentName.value = department.name;
  departmentManagers.value = (department.managers ?? (department.manager ? [department.manager] : []))
    .map(managerReference).filter(Boolean);
  departmentEmail.value = department.email || '';
  departmentAcceptsTickets.value = department.acceptsTickets ?? false;
  createOrUpdateDepartment.value = true;
}

async function saveDepartment() {
  if (savingDepartment.value) return;
  const name = departmentName.value.trim();
  if (!name) {
    Notification.addNotification('Indique el nombre del departamento', 'error', 6000);
    return;
  }

  savingDepartment.value = true;
  uiStore.startLoading();
  try {
    const allDepartments = await getAllPages(ResourceService.getDepartments);
    const normalizedName = normalizeSearchText(name.replace(/\s+/g, ' '));
    const duplicate = allDepartments.some(item =>
      item.id !== selectedDepartment.value?.id &&
      normalizeSearchText((item.name || '').trim().replace(/\s+/g, ' ')) === normalizedName
    );
    if (duplicate) {
      Notification.addNotification('Ya existe un departamento con ese nombre', 'error', 6000);
      return;
    }
    if (selectedDepartment.value) {
      await updateDepartment({ ...departmentPayload(), id: selectedDepartment.value.id });
    } else {
      await createDepartment();
    }
  } catch {
    Notification.addNotification('No se ha podido comprobar el nombre del departamento', 'error', 6000);
  } finally {
    uiStore.stopLoading();
    savingDepartment.value = false;
  }
}

async function updateDepartment(department) {
  // this.$uiStore.setLoading(true);
  uiStore.startLoading();
  await ResourceService.updateDepartment(department, department.id).then(() => {
    Notification.addNotification('Departamento actualizado correctamente', 'success', 6000);
    // this.$uiStore.setLoading(false);
    uiStore.stopLoading();
    cancel();
  }).catch(() => {
    Notification.addNotification('Error al actualizar el departamento', 'error', 6000);
    // this.$uiStore.setLoading(false);
    uiStore.stopLoading();
  }).finally(() => {
    closeCreateOrUpdateDialog();
  });
}

function reset() {
  department.value = {};
  departmentName.value = '';
  departmentManagers.value = [];
  departmentEmail.value = '';
  departmentAcceptsTickets.value = false;
  selectedDepartment.value = null;
}

function cancel() {
  getDepartments();
}

function closeCreateOrUpdateDialog() {
  createOrUpdateDepartment.value = false;
}

onMounted(() => {
  getDepartments();
  getEmployees();
});
</script>

<template>
  <div>
    <HeaderTitle title="Ajustes" />
    <div class="d-flex flex-row justify-content-start">
      <img src="@/assets/icons/group.svg" width="30" alt="">
      <div class="title-text pl-4">
        Departamentos
      </div>
    </div>
    <el-card style="height: 200px"
             class="card-search w-100 wback"
             body-style="padding:0"
    >
      <div class="row mx-5 master-search-row">
        <div class="col-12">
          <div class="row align-items-end">
            <div class="col-md-4 col-12">
              <div class="form-item date-picker label-common">
                <label class="w-100 mb-2">Nombre</label>
                <el-input v-model="searchFilterDepartmentName" placeholder="Nombre del Departamento" prefix-icon="el-icon-search" clearable />
              </div>
            </div>
            <div class="col-md-4 offset-md-4 col-12 d-flex justify-content-end master-search-actions">
              <button @click="createOrUpdateDepartment = true" class="btn btn_darkpink btn-search master-create-button">
                NUEVO DEPARTAMENTO
              </button>
            </div>
          </div>
        </div>
      </div>
    </el-card>
    <div class="table table-departments table-not-mobile">
      <div v-if="departments && departments.length > 0">
        <table>
          <thead>
          <tr>
            <th>Nombre</th>
            <th>Responsables</th>
            <th>Correo electrónico</th>
            <th>Acepta tickets</th>
            <th class="master-actions-col">Acciones</th>
          </tr>
          </thead>
          <tbody>
          <tr
              v-for="department in departments"
              :key="department.id"
              class="pointer"
              @click="editDepartment(department)"
          >
            <td>
              {{ department.name }}
            </td>
            <td>{{ departmentManagerNames(department) }}</td>
            <td>{{ department.email || '—' }}</td>
            <td>{{ department.acceptsTickets ? 'Sí' : 'No' }}</td>
            <td class="master-actions-col">
              <div class="buttons d-inline-flex">
                <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Editar"
                  @click.stop="editDepartment(department)">
                  <font-awesome-icon icon="circle" style="color: #f7d20e" />
                  <font-awesome-icon icon="plus" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
                <font-awesome-layers class="fa-2x pointer" style="margin-left: 5px" title="Eliminar"
                  @click.stop="deleteDialogShown = true; selectedDepartment = department;">
                  <font-awesome-icon icon="circle" style="color:red" />
                  <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                </font-awesome-layers>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
      <div v-else>
        <h5>No se han encontrado departamentos</h5>
      </div>


      <!--      <div class="table-card-departments">-->
      <!--        <div v-if="departments && departments.length > 0">-->
      <!--          -->
      <!--        </div>-->
      <!--      </div>-->


    </div>

    <div class="table-mobile">
      <div v-if="departments && departments.length > 0">
        <el-row v-for="department in departments" :key="department.id" class="mt-3">
          <el-card class="master-mobile-card w-100 pointer" @click="editDepartment(department)">
            <div class="master-mobile-card__delete">
              <font-awesome-layers class="fa-2x pointer" title="Eliminar" @click.stop="deleteDialogShown = true; selectedDepartment = department;">
                <font-awesome-icon icon="circle" style="color:red" />
                <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
              </font-awesome-layers>
            </div>
            <div class="master-mobile-card__title">{{ department.name }}</div>
            <div>Responsables: {{ departmentManagerNames(department) }}</div>
            <div class="master-mobile-card__email">Correo electrónico: {{ department.email || '—' }}</div>
            <div>Acepta tickets: {{ department.acceptsTickets ? 'Sí' : 'No' }}</div>
          </el-card>
        </el-row>
      </div>
      <div v-else class="mt-3">
        <h5>No se han encontrado departamentos</h5>
      </div>
    </div>

    <el-dialog
        v-model="createOrUpdateDepartment"
        class="dense-dialog department-dialog"
        @closed="reset()"
        :show-close="false"
        size="'medium'"
        width="850"
        :center="true"
        :lock-scroll="true"
        :style="{ borderRadius: `var(--el-border-radius-round)` }"
        >
        <div class="my-header">
        <svg
            id="asterisk"
            xmlns="http://www.w3.org/2000/svg"
            width="724"
            height="1024"
            viewBox="0 0 724 1024"
        >
          <path
              d="M0 499.8l59-181.7 332.5 108.6-59 180.8-332.5-107.7zM79.6 744.3l206.1-282.9 154.5 111.5-206.1 283.8-154.5-112.4zM266.9 167.3h191.1v350.3h-191.1v-350.3zM285.6 572.9l154.5-111.5 206.1 282.9-154.5 112.4-206.1-283.8zM332.5 426.8l332.5-108.6 59 181.7-331.6 107.7-59.9-180.8z"
          />
        </svg>
        <span class="text-header ml-5 mt-3">{{ selectedDepartment ? 'Actualizar Departamento' : 'Nuevo Departamento'}}</span>
        <img
            class="close-icon pointer"
            src="@/assets/images/aspa_blue_small_entrada.png"
            alt=""
            @click="closeCreateOrUpdateDialog()"
        >
      </div>

      <div class="my-body">
        <form @submit.prevent="saveDepartment">
          <div class="form-group row">
            <div class="col-12">
              <div class="form-item">
                <label for="departmentName" class="w-100">Nombre del departamento:</label>
                <el-input
                    v-model="departmentName"
                    :label="'Nombre departamento'"
                    :placeholder="'Nombre departamento'"
                />
              </div>

            </div>
            <div class="col-md-6 col-12 mt-3">
              <label for="departmentManager" class="w-100">Responsables (opcional)</label>
              <el-select id="departmentManager" v-model="departmentManagers" class="w-100" multiple filterable clearable :reserve-keyword="false" collapse-tags collapse-tags-tooltip placeholder="Seleccionar responsables">
                <el-option v-for="employee in employees" :key="employee.id" :label="employee.fullName" :value="managerReference(employee)" />
              </el-select>
            </div>
            <div class="col-md-6 col-12 mt-3">
              <label for="departmentEmail" class="w-100">Correo electrónico (opcional)</label>
              <el-input id="departmentEmail" v-model="departmentEmail" type="email" placeholder="Correo electrónico" />
            </div>
            <div class="col-12 mt-3">
              <el-switch v-model="departmentAcceptsTickets" active-text="Acepta tickets" aria-label="Acepta tickets" />
            </div>
            <div class="col-md-4 offset-md-5 col-12 d-flex flex-column justify-content-end align-items-end mt-3 master-dialog-actions">
              <button class="btn btn_darkpink" type="submit" :disabled="savingDepartment">
                {{ selectedDepartment ? 'Actualizar' : 'Nuevo'}}
              </button>
            </div>
          </div>
        </form>
      </div>
    </el-dialog>

    <SharedDeleteConfirmDialog
      v-model="deleteDialogShown"
      title="Eliminar departamento"
      message="¿Desea eliminar permanentemente este departamento?"
      @confirm="deleteDepartment(selectedDepartment)"
      @cancel="selectedDepartment = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.my-body.my-body { margin-right: 0; }
.master-search-row .form-item { margin-bottom: 0; }
.master-create-button { width: 100%; max-width: 300px; min-height: 44px; margin-bottom: 0 !important; color: white; }
@media (max-width: 767px) { .master-search-row .form-item { margin-bottom: 12px; } }

.department-input {
  /*display: flex;*/
  vertical-align: baseline;
  margin: 30px 10px 10px 10px;
}

.table-departments {
  table {
    //margin-left: auto;
    //margin-right: auto;
    //width: auto;
  }
}

.table-mobile {
  display: none;
}

.master-mobile-card {
  border-radius: 16px;
}

.master-mobile-card :deep(.el-card__body) {
  position: relative;
  padding: 16px 18px;
}

.master-mobile-card__delete {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 1;
}

.master-mobile-card__title {
  color: $bg-blue;
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
  padding-right: 42px;
  word-break: break-word;
}

.master-mobile-card__email {
  overflow-wrap: anywhere;
}

.master-actions-col {
  width: 110px;
  min-width: 110px;
  text-align: center;
}



.el-dialog__body {
  background: #f3f0fb !important;
  min-height: 800px !important;

  .el-dialog__header {
    padding: 0;
  }

  .my-header {
    margin-top: 2px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    .text-header {
      text-align: left;
      font-size: 4em;
      font-weight: 900;
      letter-spacing: -0.05em;
      color: #f9156f;
    }

    .close-icon {
      width: 8%;
      height: 20%;
    }

    .inter-icon {
      width: 2%;
      height: 2%;
    }

    #asterisk {
      position: absolute;
      vertical-align: super;
      height: auto;
      width: 2.2em;
      fill: #f9156f;
    }

  }

}

@media (max-width: 767px) {
  .title-text {
    font-size: 2.2em;
  }

  .card-search {
    height: auto !important;
    padding: 20px 16px;
    background-size: 92px;
    background-position: calc(100% + 16px) 12px;
  }

  .master-search-row {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .master-search-actions {
    width: 100%;
    flex-direction: column;
    align-items: stretch !important;
    gap: 10px;
    margin-top: 10px;
  }

  .master-search-actions .btn-search {
    width: 100%;
    max-width: 100% !important;
    min-width: 100% !important;
  }

  .table-not-mobile {
    display: none;
  }

  .table-mobile {
    display: block;
    padding-right: 14px;
  }

  :deep(.department-dialog) {
    width: calc(100vw - 24px) !important;
    max-width: calc(100vw - 24px);
    margin-top: 4vh !important;
  }

  :deep(.department-dialog .el-dialog__body) {
    min-height: auto !important;
  }

  .el-dialog__body .my-header {
    align-items: flex-start;
    gap: 12px;
    padding-right: 10px;
  }

  .el-dialog__body .my-header .text-header {
    font-size: 2em;
    line-height: 1.1;
    margin-top: 0.75rem !important;
  }

  .el-dialog__body .my-header .close-icon {
    width: 26px;
    min-width: 26px;
    margin-top: 12px;
  }

  .el-dialog__body .my-body {
    margin-right: 0;
  }

  .master-dialog-actions {
    align-items: stretch !important;
    margin-left: 0 !important;
  }

  .master-dialog-actions .btn {
    width: 100%;
  }
}
</style>
